import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import axios from "axios";

const apiBaseUrl =
  process.env.REACT_APP_API_URL ||
  (typeof window !== "undefined" && window.location.origin.includes("localhost")
    ? "http://localhost:5000"
    : "");

if (apiBaseUrl) {
  axios.defaults.baseURL = apiBaseUrl;
}
// Add token to requests if available
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const getProfile = useCallback(async () => {
    try {
      const response = await axios.get("/api/auth/profile");

      // Ensure isAdmin is properly set
      const userData = {
        _id: response.data._id,
        name: response.data.name,
        email: response.data.email,
        isAdmin: Boolean(response.data.isAdmin),
      };

      setUser(userData);
      // Update stored token so admin role is in sync (e.g. if user was made admin after login)
      if (response.data.token) {
        localStorage.setItem("token", response.data.token);
      }
    } catch (error) {
      // Token invalid, logout
      localStorage.removeItem("token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Check if user is logged in on app start
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      // Verify token and get user data
      getProfile();
    } else {
      setLoading(false);
    }
  }, [getProfile]);

  const login = async (email, password) => {
    try {
      const response = await axios.post("/api/auth/login", { email, password });
      // Check if email verification is needed
      if (response.data.needVerification) {
        return {
          success: false,
          needVerification: true,
          email: response.data.email,
          message: response.data.message,
        };
      }
      const { token, ...userData } = response.data.data;

      // Ensure isAdmin is properly set
      const user = {
        _id: userData._id,
        name: userData.name,
        email: userData.email,
        isAdmin: Boolean(userData.isAdmin),
      };

      localStorage.setItem("token", token);
      setUser(user);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Login failed",
      };
    }
  };

  const register = async (name, email, password) => {
    try {
      const response = await axios.post("/api/auth/register", {
        name,
        email,
        password,
        confirmPassword: password,
      });
      // If verification needed, return needVerification
      if (response.data.needVerification) {
        return {
          success: true,
          needVerification: true,
          email: response.data.email,
        };
      }
      return { success: false, message: "Unexpected response" };
    } catch (error) {
      const data = error.response?.data;
      if (data?.emailExistsUnverified && data?.email) {
        return {
          success: false,
          emailExistsUnverified: true,
          email: data.email,
          message: data.message || "This email is pending verification.",
        };
      }
      return {
        success: false,
        message: data?.message || "Registration failed",
      };
    }
  };

  const verifyEmail = async (email, code) => {
    try {
      const response = await axios.post("/api/auth/verify-email", {
        email,
        code,
      });
      const { token, ...userData } = response.data.data;
      const user = {
        _id: userData._id,
        name: userData.name,
        email: userData.email,
        isAdmin: userData.isAdmin === true || userData.isAdmin === "true",
      };
      localStorage.setItem("token", token);
      setUser(user);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Verification failed",
      };
    }
  };

  const resendVerifyCode = async (email) => {
    try {
      await axios.post("/api/auth/resend-verify-code", { email });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Failed to resend code",
      };
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
  };

  const forgotPassword = async (email) => {
    try {
      console.log("Sending forgot password request for:", email);
      const response = await axios.post("/api/auth/forgot-password", { email });
      console.log("Response:", response);
      return { success: true };
    } catch (error) {
      console.error("Forgot password error:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to send reset email",
      };
    }
  };

  const resetPassword = async (token, password) => {
    try {
      await axios.put(`/api/auth/reset-password/${token}`, { password });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Password reset failed",
      };
    }
  };

  const value = {
    user,
    login,
    register,
    verifyEmail,
    resendVerifyCode,
    logout,
    forgotPassword,
    resetPassword,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
